import { IconHeartFilled, IconPlayerSkipForward, IconX } from '@tabler/icons-react-native'
import { clsx } from 'clsx'
import { forwardRef, type ComponentProps, type ComponentRef } from 'react'

import { Icon, Pressable, SparkIcon } from '@/components'
import { COLOR_WHITE, shadowStyles } from '@/lib'

type ProfileActionVariant = 'dislike' | 'like' | 'spark' | 'skip'
type ProfileActionSize = 'normal' | 'large'

interface VariantConfig {
  IconComponent: ComponentProps<typeof Icon>['as']
  colorClassName: string
  // Optional per-variant nudge combined with the size's icon class (e.g. optically centering the heart).
  iconClassName?: string
  iconProps: { fill?: string; stroke?: string; strokeWidth?: number }
  shadow: (typeof shadowStyles)[keyof typeof shadowStyles]
}

// The circular action buttons shared by the Search feed and a searched profile's detail screen, so
// the two surfaces stay identical. The feed emphasises Like/Spark with `size='large'`; the detail
// screen leaves every button at the default `normal` size so they all match.
const VARIANTS: Record<ProfileActionVariant, VariantConfig> = {
  dislike: {
    IconComponent: IconX,
    colorClassName: 'bg-brand-black-500 hover:bg-brand-black-400',
    iconProps: { stroke: COLOR_WHITE, strokeWidth: 3 },
    shadow: shadowStyles.blackSoft
  },
  like: {
    IconComponent: IconHeartFilled,
    colorClassName: 'bg-brand-orange-500 hover:bg-brand-orange-400',
    iconClassName: 'mt-1',
    iconProps: { fill: COLOR_WHITE, stroke: 'none', strokeWidth: 0 },
    shadow: shadowStyles.orangeGlow
  },
  spark: {
    IconComponent: SparkIcon,
    colorClassName: 'bg-brand-yellow-600 hover:bg-brand-yellow-500',
    iconProps: { fill: COLOR_WHITE, stroke: 'none', strokeWidth: 0 },
    shadow: shadowStyles.yellowGlow
  },
  skip: {
    IconComponent: IconPlayerSkipForward,
    colorClassName: 'bg-brand-black-500 hover:bg-brand-black-400',
    iconProps: { fill: COLOR_WHITE, stroke: COLOR_WHITE, strokeWidth: 3 },
    shadow: shadowStyles.blackSoft
  }
}

export interface ProfileActionButtonProps extends Omit<ComponentProps<typeof Pressable>, 'children'> {
  size?: ProfileActionSize
  variant: ProfileActionVariant
}

export const ProfileActionButton = forwardRef<ComponentRef<typeof Pressable>, ProfileActionButtonProps>(
  ({ className, disabled, size = 'normal', variant, ...props }, ref) => {
    const { IconComponent, colorClassName, iconClassName, iconProps, shadow } = VARIANTS[variant]

    return (
      <Pressable
        ref={ref}
        accessibilityRole='button'
        accessibilityState={{ busy: !!disabled, disabled: !!disabled }}
        className={clsx(
          'items-center justify-center rounded-full',
          size === 'normal' ? 'h-16 w-16' : 'h-20 w-20',
          colorClassName,
          className
        )}
        disabled={disabled}
        style={shadow}
        {...props}
      >
        <Icon
          as={IconComponent}
          className={clsx(size === 'normal' ? 'h-1/3 w-1/3' : 'h-1/2 w-1/2', iconClassName)}
          {...iconProps}
        />
      </Pressable>
    )
  }
)

ProfileActionButton.displayName = 'ProfileActionButton'
